import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Typography,
  Box
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

const MIN_OVERRIDE_LENGTH = 20;

const WorkOrderCancelDialog = ({
  open,
  onClose,
  workOrder,
  onSuccess,
  requiresAmOverride = false
}) => {
  const [cancellationReason, setCancellationReason] = useState('');
  const [overrideJustification, setOverrideJustification] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  const handleClose = () => {
    setCancellationReason('');
    setOverrideJustification('');
    setError('');
    onClose();
  };

  const handleSubmit = async () => {
    const reason = cancellationReason.trim();
    const justification = overrideJustification.trim();

    if (!reason) {
      setError('Cancellation reason is required.');
      return;
    }
    if (requiresAmOverride && justification.length < MIN_OVERRIDE_LENGTH) {
      setError(`Override justification must be at least ${MIN_OVERRIDE_LENGTH} characters.`);
      return;
    }

    try {
      setLoading(true);
      setError('');
      await axios.put(`/api/maintenance/${workOrder._id}/cancel`, {
        cancellationReason: reason,
        ...(requiresAmOverride ? { overrideJustification: justification } : {})
      });
      showToast('Work order cancelled successfully', 'success');
      onSuccess();
      handleClose();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to cancel work order';
      setError(message);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cancel Work Order</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Work Order: <strong>{workOrder?.title}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Aircraft: <strong>{workOrder?.aircraftId?.registration || 'Unknown'}</strong>
          </Typography>
        </Box>

        <Alert severity="warning" sx={{ mb: 2 }}>
          Cancelling permanently stops the workflow. Linked parts procurement work orders will also be cancelled.
        </Alert>

        {requiresAmOverride && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Accountable Manager override justification is required for this cancellation.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          required
          multiline
          rows={3}
          label="Cancellation Reason"
          value={cancellationReason}
          onChange={(e) => setCancellationReason(e.target.value)}
          placeholder="Explain why this work order is being cancelled..."
          sx={{ mb: 2 }}
        />

        {requiresAmOverride && (
          <TextField
            fullWidth
            required
            multiline
            rows={3}
            label="Override Justification"
            value={overrideJustification}
            onChange={(e) => setOverrideJustification(e.target.value)}
            placeholder="Explain why Accountable Manager override is required..."
            helperText={`${overrideJustification.trim().length}/${MIN_OVERRIDE_LENGTH} characters minimum`}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Close
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="error"
          disabled={loading || !cancellationReason.trim() || (requiresAmOverride && overrideJustification.trim().length < MIN_OVERRIDE_LENGTH)}
        >
          {loading ? 'Cancelling...' : 'Cancel Work Order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkOrderCancelDialog;
